import { useState } from "react";
import { cn } from "utils/helpers";
import TextBoxHTML from "newui/components/TextBoxHTML";
import type { ReviewClarification } from "../../../../types/clarification-review.type";

interface ChangeTrackingViewProps {
	clarification: ReviewClarification;
	className?: string;
}

export const ChangeTrackingView: React.FC<ChangeTrackingViewProps> = ({
	clarification,
	className,
}) => {
	const [hoveredSide, setHoveredSide] = useState<"original" | "edited" | null>(null);

	const originalText = clarification.original_clarification_text ?? clarification.clarification_text;
	const editedText = clarification.clarification_text;
	const hasChanges = originalText !== editedText;

	if (!hasChanges) {
		return (
			<div className={cn("rounded-lg border border-gray-200 p-4", className)} data-testid="change-tracking-no-changes">
				<p className="mb-1 text-xs font-medium uppercase tracking-wider text-gray-400">
					Clarification Text
				</p>
				<TextBoxHTML html={editedText} />
			</div>
		);
	}

	const panelBase = "flex-1 rounded-lg border p-4 transition-colors";

	return (
		<div className={cn("space-y-2", className)} data-testid="change-tracking-view">
			<div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
				<div
					className={cn(
						panelBase,
						"border-gray-200",
						hoveredSide === "original" && "bg-red-50 border-red-200",
						hoveredSide === "edited" && "bg-gray-50",
					)}
					onMouseEnter={() => setHoveredSide("original")}
					onMouseLeave={() => setHoveredSide(null)}
					data-testid="change-tracking-original"
				>
					<p className="mb-2 text-xs font-medium uppercase tracking-wider text-gray-500">
						Original
					</p>
					<div className={cn("text-sm text-gray-600", hoveredSide === "original" && "line-through")}>
						<TextBoxHTML html={originalText} />
					</div>
				</div>

				<div
					className={cn(
						panelBase,
						"border-gray-200",
						hoveredSide === "edited" && "bg-green-50 border-green-200",
						hoveredSide === "original" && "bg-gray-50",
					)}
					onMouseEnter={() => setHoveredSide("edited")}
					onMouseLeave={() => setHoveredSide(null)}
					data-testid="change-tracking-edited"
				>
					<p className="mb-2 text-xs font-medium uppercase tracking-wider text-gray-500">
						Edited
					</p>
					<div className="text-sm text-gray-700">
						<TextBoxHTML html={editedText} />
					</div>
					{clarification.edited_by_name && (
						<p className="mt-2 text-xs text-gray-400" data-testid="change-tracking-edited-by">
							Edited by {clarification.edited_by_name}
							{clarification.edited_at && ` on ${new Date(clarification.edited_at).toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric" })}`}
						</p>
					)}
				</div>
			</div>
		</div>
	);
};
